import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Box, InlineStack, TextField, DropZone, Text, Button, Icon, Spinner, ButtonGroup } from '@shopify/polaris';
import { MagicMinor, UploadMajor, ChevronLeftMinor, ChevronRightMinor, ImportMinor, StarFilledMinor, ExploreImagesMajor, MaximizeMinor } from '@shopify/polaris-icons';
import { BlobServiceClient } from '../services/blobStorageCompat';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';


const SnapsAIEditPage2 = forwardRef(function SnapsAIEditPage2(
  {
    embedded = false,
    sceneIndex,
    storyName = '',
    sceneName = '',
    initialPrompt = '',
    initialOutputImageUrl = '',
    initialExpandDirection = '',
    initialPreviewType = '',
    initialDesignId = '',
    setToastMessage,
    setToastActive,
    enableExpandControls = false,
    enableUpscaleControls = false,
    recentType = '',
    sceneText,
    onSceneTextChange,
    renderOverlay,
    renderActions,
    sceneCharacters,
  },
  ref
) {
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [multiText, setMultiText] = useState('');
  const [recentDesigns, setRecentDesigns] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [recentStartIndex, setRecentStartIndex] = useState(0); // window start for visible 10
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [azureSettings, setAzureSettings] = useState(null);
  const [uploadedFileAzureUrl, setUploadedFileAzureUrl] = useState('');
  const [uploadedFileKey, setUploadedFileKey] = useState('');
  const hasInitializedFromPropsRef = useRef(false);
  const [selectedDesignId, setSelectedDesignId] = useState('');
  const [isFavouriting, setIsFavouriting] = useState(false);
  const [upscaleModel, setUpscaleModel] = useState('anime');

  const [expandDirection, setExpandDirection] = useState('left');
  // Fixed overlay position should reflect the expand_direction of the CURRENT preview image,
  // not the next-action button state.
  const [previewExpandDirection, setPreviewExpandDirection] = useState('');
  const [previewType, setPreviewType] = useState(''); // 'design' | 'expand' | 'upscale' | ''
  const lastActionRef = useRef(''); // 'design' | 'expand' | 'upscale' | ''
  const previewContainerRef = useRef(null);
  const [imageRect, setImageRect] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [sliderRatio, setSliderRatio] = useState(0.5);

  const handleDownloadPreview = useCallback(async () => {
    if (!selectedPreviewUrl) return;
    try {
      const resp = await fetch(selectedPreviewUrl, { mode: 'cors' });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = (selectedPreviewUrl.split('/').pop() || 'image').split('?')[0] || 'image';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_ignore) { }
  }, [selectedPreviewUrl]);


  const handleDrop = useCallback((droppedFiles, acceptedFiles) => {
    const files = acceptedFiles || droppedFiles || [];
    setUploadedFiles(files);
    const first = files?.[0];
    const newKey = first ? `${first.name}|${first.size}|${first.lastModified}` : '';
    if (newKey !== uploadedFileKey) {
      setUploadedFileAzureUrl('');
      setUploadedFileKey(newKey);
    }
  }, [uploadedFileKey]);

  const firstPreviewUrl = uploadedFiles?.[0]
    ? URL.createObjectURL(uploadedFiles[0])
    : undefined;

  // Note: we clear uploader when user types a URL via onChange handler below to avoid race with uploads

  // Clear input URL and right preview when a file is uploaded, but DO NOT clear uploadedFiles
  useEffect(() => {
    if (uploadedFiles.length > 0) {
      if (imageUrl) setImageUrl('');
      if (selectedPreviewUrl) setSelectedPreviewUrl('');
    }
  }, [uploadedFiles.length]);

  // Load last 10 recent designs, filtered by story/scene when provided
  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setIsLoadingRecent(true);
        const baseUrl = getServerURL();
        const params = new URLSearchParams({ limit: '10' });
        if (recentType) params.set('type', recentType);
        if (storyName) params.set('story', storyName);
        if (sceneName) params.set('scene', sceneName);
        const resp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
          headers: authService.getAuthHeaders(),
        });
        if (!resp.ok) return;
        const json = await resp.json();
        const items = Array.isArray(json?.data) ? json.data : [];
        setRecentDesigns(items);
      } catch (_err) {
        setRecentDesigns([]);
      } finally {
        setIsLoadingRecent(false);
      }
    };
    fetchRecent();
  }, [storyName, sceneName, recentType]);

  // Load Azure settings (design designer settings endpoint)
  const loadAzureSettings = useCallback(async () => {
    try {
      const baseUrl = getServerURL();
      const resp = await fetch(`${baseUrl}/settings/storage/client`, {
        headers: authService.getAuthHeaders(),
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      if (json?.success && json?.data && json.data.AZURE_BLOB_BASE_URL && json.data.AZURE_SAS_TOKEN) {
        setAzureSettings(json.data);
        return json.data;
      }
      return null;
    } catch (_e) {
      return null;
    }
  }, []);

  // Prefill state from initial props once per mount
  useEffect(() => {
    if (hasInitializedFromPropsRef.current) return;
    const prompt = initialPrompt || '';
    const outputUrl = initialOutputImageUrl || '';
    if (prompt || outputUrl) {
      setMultiText(prompt);
      if (outputUrl) {
        setImageUrl(outputUrl);
        setSelectedPreviewUrl(outputUrl);
      }
    }
    if (initialDesignId) {
      setSelectedDesignId(initialDesignId);
    }
    const dir = (initialExpandDirection || '').toLowerCase();
    if (dir === 'left' || dir === 'right') {
      setExpandDirection(dir);
    }
    const ptype = (initialPreviewType || '').toLowerCase();
    if (ptype === 'expand' || ptype === 'design' || ptype === 'upscale') {
      setPreviewType(ptype);
      lastActionRef.current = ptype;
    }
    // If initial state represents an expand preview, lock overlay to that direction
    if ((ptype === 'expand') && (dir === 'left' || dir === 'right')) {
      setPreviewExpandDirection(dir);
    }
    hasInitializedFromPropsRef.current = true;
    // intentionally run once per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAzureSettings();
  }, [loadAzureSettings]);

  // Compute displayed image rect (contain) inside preview container
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;

    let resizeObserver;
    let cancelled = false;

    const compute = (imgNaturalWidth, imgNaturalHeight) => {
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      if (!cw || !ch || !imgNaturalWidth || !imgNaturalHeight) {
        if (!cancelled) setImageRect({ left: 0, top: 0, width: 0, height: 0 });
        return;
      }
      const scale = Math.min(cw / imgNaturalWidth, ch / imgNaturalHeight);
      const dw = imgNaturalWidth * scale;
      const dh = imgNaturalHeight * scale;
      const left = (cw - dw) / 2;
      const top = (ch - dh) / 2;
      if (!cancelled) setImageRect({ left, top, width: dw, height: dh });
    };

    const loadAndCompute = () => {
      if (!selectedPreviewUrl) {
        setImageRect({ left: 0, top: 0, width: 0, height: 0 });
        return;
      }
      const img = new Image();
      img.onload = () => {
        compute(img.naturalWidth || img.width, img.naturalHeight || img.height);
      };
      img.onerror = () => {
        compute(0, 0);
      };
      img.src = selectedPreviewUrl;
    };

    loadAndCompute();

    resizeObserver = new ResizeObserver(() => {
      loadAndCompute();
    });
    resizeObserver.observe(container);

    window.addEventListener('resize', loadAndCompute);

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', loadAndCompute);
    };
  }, [selectedPreviewUrl]);

  const uploadFileToAzure = useCallback(async (file) => {
    if (!azureSettings) throw new Error('Azure settings not loaded');
    const { AZURE_BLOB_BASE_URL, AZURE_SAS_TOKEN } = azureSettings;
    const containerUrl = AZURE_BLOB_BASE_URL + AZURE_SAS_TOKEN;
    const blobServiceClient = new BlobServiceClient(containerUrl);
    const containerClient = blobServiceClient.getContainerClient("");
    const ext = (file.name?.split('.').pop() || 'png').toLowerCase();
    const randomTxt = `${Math.random().toString(36).substring(2, 8)}${Date.now()}`;
    const newFileName = `design_input_${randomTxt}.${ext}`;
    const blockBlobClient = containerClient.getBlockBlobClient(newFileName);
    const { publicUrl } = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type || 'image/png' },
    });
    return publicUrl;
  }, [azureSettings]);

  const handleGenerate = useCallback(async () => {
    if (isGenerating) return;
    const baseUrl = getServerURL();
    let finalImageUrl = (imageUrl || '').trim();
    try {
      setIsGenerating(true);
      if (!finalImageUrl) {
        if (uploadedFiles.length === 0) {
          // Nothing to generate with
          setIsGenerating(false);
          return;
        }
        // Upload the first file to Azure and use its URL
        const first = uploadedFiles[0];
        const currentKey = `${first.name}|${first.size}|${first.lastModified}`;
        if (uploadedFileAzureUrl && currentKey === uploadedFileKey) {
          finalImageUrl = uploadedFileAzureUrl;
        } else {
          if (!azureSettings) {
            const settings = await loadAzureSettings();
            if (!settings) {
              throw new Error('Azure settings not available');
            }
          }
          finalImageUrl = await uploadFileToAzure(first);
          setUploadedFileAzureUrl(finalImageUrl);
          setUploadedFileKey(currentKey);
        }
      }

      // POST to nano banana 2
      const resp = await fetch(`${baseUrl}/nano-banana-2`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: finalImageUrl,
          prompt: multiText || '',
        }),
      });

      // Parse response for new API shape and update preview
      let apiOutputUrl = '';
      let apiOriginalUrl = '';
      try {
        const data = await resp.clone().json();
        const imagesArr = data?.data?.images;
        if (Array.isArray(imagesArr) && imagesArr.length > 0) {
          apiOutputUrl = imagesArr[0]?.url || '';
        }
        apiOriginalUrl = data?.data?.originalImageUrl || '';
      } catch (_ignore) { }

      // Update inputs and preview
      setImageUrl(apiOriginalUrl || finalImageUrl);
      setSelectedPreviewUrl(apiOutputUrl || apiOriginalUrl || finalImageUrl);

      // Mark current preview as a design
      setPreviewType('design');
      lastActionRef.current = 'design';
      // Design preview has no expand side
      setPreviewExpandDirection('');

      // Optionally refresh the recent grid
      try {
        const params = new URLSearchParams({ limit: '10' });
        if (recentType) params.set('type', recentType);
        if (storyName) params.set('story', storyName);
        if (sceneName) params.set('scene', sceneName);
        const recentResp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
          headers: authService.getAuthHeaders(),
        });
        if (recentResp.ok) {
          const recentJson = await recentResp.json();
          const items = Array.isArray(recentJson?.data) ? recentJson.data : [];
          setRecentDesigns(items);
        }
      } catch (_e) { }

      // Optionally check response (no-op if non-200)
    } catch (_err) {
      // Could add user feedback here
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, imageUrl, uploadedFiles, multiText, uploadFileToAzure]);

  const handleAddToFavourites = useCallback(async () => {
    if (isFavouriting) return;
    try {
      setIsFavouriting(true);
      if (!selectedDesignId) {
        if (typeof setToastMessage === 'function' && typeof setToastActive === 'function') {
          setToastMessage('No design selected yet');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 2000);
        }
        return;
      }
      const baseUrl = getServerURL();
      await fetch(`${baseUrl}/design/favorites`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ design_id: selectedDesignId }),
      });
      if (typeof setToastMessage === 'function' && typeof setToastActive === 'function') {
        setToastMessage('Added to favourites');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 2000);
      }
    } catch (_err) {
    } finally {
      setIsFavouriting(false);
    }
  }, [selectedDesignId, isFavouriting]);



  const handleExpand = useCallback(async (options = {}) => {
    if (isGenerating) return;
    const baseUrl = getServerURL();
    let finalImageUrl = (imageUrl || '').trim();
    try {
      setIsGenerating(true);
      if (!finalImageUrl) {
        if (uploadedFiles.length > 0) {
          const first = uploadedFiles[0];
          const currentKey = `${first.name}|${first.size}|${first.lastModified}`;
          if (uploadedFileAzureUrl && currentKey === uploadedFileKey) {
            finalImageUrl = uploadedFileAzureUrl;
          } else {
            if (!azureSettings) {
              const settings = await loadAzureSettings();
              if (!settings) {
                throw new Error('Azure settings not available');
              }
            }
            finalImageUrl = await uploadFileToAzure(first);
            setUploadedFileAzureUrl(finalImageUrl);
            setUploadedFileKey(currentKey);
          }
        } else if (selectedPreviewUrl) {
          // Fallback to using current preview image when no input URL or upload is present
          finalImageUrl = selectedPreviewUrl;
        } else {
          setIsGenerating(false);
          return;
        }
      }

      const payload = {
        imageUrl: finalImageUrl,
        prompt: multiText || '',
        story: storyName || '',
        scene: sceneName || '',
        expand_direction: expandDirection,
        scene_text: typeof options.sceneText === 'string' ? options.sceneText : undefined,
      };
      const resp = await fetch(`${baseUrl}/design/expand`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        try {
          const clone = resp.clone();
          let serverMessage = '';
          try {
            const j = await resp.json();
            serverMessage = j?.message || j?.error || j?.errors?.[0]?.message || '';
          } catch (_) {
            const t = await clone.text();
            serverMessage = (t || '').slice(0, 500);
          }
          if (typeof setToastMessage === 'function' && typeof setToastActive === 'function') {
            setToastMessage(serverMessage || `Expand failed (${resp.status})`);
            setToastActive(true);
            setTimeout(() => setToastActive(false), 2500);
          }
        } catch (_) { }
        setIsGenerating(false);
        return;
      }

      let apiOutputUrl = '';
      let apiDesignId = '';
      try {
        const data = await resp.clone().json();
        apiOutputUrl = data?.imageUrl || data?.data?.imageUrl || '';
        apiDesignId = data?._id || data?.id || data?.designId || data?.data?._id || data?.data?.id || data?.data?.designId || '';
      } catch (_ignore) { }

      setImageUrl(finalImageUrl);
      setSelectedPreviewUrl(apiOutputUrl || finalImageUrl);
      setPreviewType('expand');
      lastActionRef.current = 'expand';
      // Lock overlay position to the expand direction used for this generated preview
      setPreviewExpandDirection(expandDirection);
      // Force overlay recompute on next tick so parent overlay uses latest imageRect
      setTimeout(() => {
        try {
          const container = previewContainerRef.current;
          if (container) {
            const event = new Event('resize');
            window.dispatchEvent(event);
          }
        } catch (_e) { }
      }, 0);
      if (apiDesignId) setSelectedDesignId(apiDesignId);

      try {
        const params = new URLSearchParams({ limit: '10' });
        if (recentType) params.set('type', recentType);
        if (storyName) params.set('story', storyName);
        if (sceneName) params.set('scene', sceneName);
        const recentResp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
          headers: authService.getAuthHeaders(),
        });
        if (recentResp.ok) {
          const recentJson = await recentResp.json();
          const items = Array.isArray(recentJson?.data) ? recentJson.data : [];
          setRecentDesigns(items);
        }
      } catch (_e) { }
    } catch (_err) {
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, imageUrl, uploadedFiles, multiText, expandDirection, uploadFileToAzure]);

  const handleUpscale = useCallback(async () => {
    if (isGenerating) return;
    const baseUrl = getServerURL();
    let finalImageUrl = (imageUrl || '').trim();
    try {
      setIsGenerating(true);
      if (!finalImageUrl) {
        if (uploadedFiles.length === 0) {
          setIsGenerating(false);
          return;
        }
        const first = uploadedFiles[0];
        const currentKey = `${first.name}|${first.size}|${first.lastModified}`;
        if (uploadedFileAzureUrl && currentKey === uploadedFileKey) {
          finalImageUrl = uploadedFileAzureUrl;
        } else {
          if (!azureSettings) {
            const settings = await loadAzureSettings();
            if (!settings) {
              throw new Error('Azure settings not available');
            }
          }
          finalImageUrl = await uploadFileToAzure(first);
          setUploadedFileAzureUrl(finalImageUrl);
          setUploadedFileKey(currentKey);
        }
      }

      const resp = await fetch(`${baseUrl}/design/upscale`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: finalImageUrl,
          upscale_factor: 1,
          model: upscaleModel,
          story: storyName || '',
          scene: sceneName || '',
          scene_text: typeof sceneText === 'string' ? sceneText : undefined,
        }),
      });

      let apiOutputUrl = '';
      let apiDesignId = '';
      try {
        const data = await resp.clone().json();
        const candidates = [data?.data, data?.result, data?.design, data?.data?.design, data];
        for (const c of candidates) {
          if (!c || typeof c !== 'object') continue;
          if (!apiOutputUrl) {
            apiOutputUrl = c.imageUrl || c.outputImageUrl || '';
          }
          if (!apiDesignId) {
            apiDesignId = c._id || c.id || c.designId || c.design_id || '';
          }
          if (apiOutputUrl && apiDesignId) break;
        }
      } catch (_ignore) { }

      setImageUrl(finalImageUrl);
      setSelectedPreviewUrl(apiOutputUrl || finalImageUrl);
      setPreviewType('');
      lastActionRef.current = '';
      // Not an expand preview
      setPreviewExpandDirection('');
      if (apiDesignId) setSelectedDesignId(apiDesignId);

      try {
        const params = new URLSearchParams({ limit: '10' });
        if (recentType) params.set('type', recentType);
        if (storyName) params.set('story', storyName);
        if (sceneName) params.set('scene', sceneName);
        const recentResp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
          headers: authService.getAuthHeaders(),
        });
        if (recentResp.ok) {
          const recentJson = await recentResp.json();
          const items = Array.isArray(recentJson?.data) ? recentJson.data : [];
          setRecentDesigns(items);
        }
      } catch (_e) { }
    } catch (_err) {
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, imageUrl, uploadedFiles, uploadFileToAzure, upscaleModel]);

  // If preview image changes due to async update, ensure overlay mode follows the last action
  useEffect(() => {
    if (!selectedPreviewUrl) return;
    if (lastActionRef.current === 'expand') setPreviewType('expand');
    else if (lastActionRef.current === 'design') setPreviewType('design');
    else if (lastActionRef.current === 'upscale') setPreviewType('upscale');
  }, [selectedPreviewUrl]);

  const handleExpandClick = useCallback(() => {
    const payloadSceneText = typeof sceneText === 'string' ? sceneText : undefined;
    return handleExpand({ sceneText: payloadSceneText });
  }, [handleExpand, sceneText]);

  // Expose imperative API for parent (generate all scenes)
  useImperativeHandle(ref, () => ({
    generate: handleGenerate,
    expand: handleExpand,
    upscale: handleUpscale,
    getPreviewUrl: () => selectedPreviewUrl || imageUrl || '',
    getInputUrl: () => imageUrl || '',
    getUpscaleModel: () => upscaleModel || 'anime',
    getSelectedDesignId: () => selectedDesignId || '',
    getPrompt: () => (multiText || ''),
    getExpandDirection: () => (expandDirection || 'left'),
    setPreviewFromUpscale: ({ inputUrl, outputUrl, designId } = {}) => {
      if (typeof inputUrl === 'string' && inputUrl) setImageUrl(inputUrl);
      if (typeof outputUrl === 'string' && outputUrl) setSelectedPreviewUrl(outputUrl);
      if (typeof designId === 'string' && designId) setSelectedDesignId(designId);
      setPreviewType('upscale');
      lastActionRef.current = 'upscale';
      setSliderRatio(0.5);
    },
    setPreviewFromExpand: ({ inputUrl, outputUrl, designId } = {}) => {
      if (typeof inputUrl === 'string' && inputUrl) setImageUrl(inputUrl);
      if (typeof outputUrl === 'string' && outputUrl) setSelectedPreviewUrl(outputUrl);
      if (typeof designId === 'string' && designId) setSelectedDesignId(designId);
      setPreviewType('expand');
      lastActionRef.current = 'expand';
    }
  }), [handleGenerate, handleExpand, selectedPreviewUrl, imageUrl, selectedDesignId]);

  const handleRecentClick = useCallback((item) => {
    if (!item) return;
    const outputUrl = item.outputImageUrl || item.inputImageUrl || '';
    const inputUrl = item.inputImageUrl || item.imageUrl || '';
    // Clear and set appropriate URLs
    setUploadedFiles([]);
    setMultiText(item.prompt || '');
    if (typeof onSceneTextChange === 'function') {
      onSceneTextChange(item.scene_text || item?.data?.scene_text || '');
    }
    const t = (item?.type || item?.action || item?.designType || item?.data?.type || '').toLowerCase();
    if (t === 'upscale' && (inputUrl || outputUrl)) {
      setImageUrl(inputUrl || outputUrl);
      setSelectedPreviewUrl(outputUrl || inputUrl);
      setPreviewType('upscale');
      lastActionRef.current = 'upscale';
      setSliderRatio(0.5);
    } else {
      setImageUrl(outputUrl);
      setSelectedPreviewUrl(outputUrl);
      if (t === 'expand') {
        setPreviewType('expand');
        lastActionRef.current = 'expand';
      } else if (t === 'design') {
        setPreviewType('design');
        lastActionRef.current = 'design';
      } else {
        setPreviewType('');
        lastActionRef.current = '';
      }
    }
    const dir = (item?.expand_direction || item?.expandDirection || item?.data?.expand_direction || '').toLowerCase();
    if (dir === 'left' || dir === 'right') {
      // Keep next action buttons synced
      setExpandDirection(dir);
      // If this recent item is an expand preview, lock overlay to that direction
      if (t === 'expand') {
        setPreviewExpandDirection(dir);
      } else {
        setPreviewExpandDirection('');
      }
    } else {
      setPreviewExpandDirection('');
    }
    if (item?._id) setSelectedDesignId(item._id);

    // Fetch full design by id to refresh scene_text from database
    (async () => {
      try {
        const designId = item?._id || item?.id || '';
        if (!designId) return;
        const baseUrl = getServerURL();
        const resp = await fetch(`${baseUrl}/design/${designId}`, {
          headers: authService.getAuthHeaders(),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const sceneTxt = data?.scene_text || data?.data?.scene_text || '';
        if (typeof onSceneTextChange === 'function' && typeof sceneTxt === 'string') {
          onSceneTextChange(sceneTxt);
        }
      } catch (_e) { }
    })();
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <InlineStack gap="400" wrap={false}>
        <Box
          padding="400"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="0"
          minHeight="1024px"
          style={{ width: '33.3333%', maxHeight: embedded ? '70vh' : '100vh', display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto' }}
          className="Polaris-ShadowBevel"
        >
          <div style={{ marginBottom: 12 }}>
            <Text variant="headingMd" as="h2">Reference Image</Text>
          </div>
          {sceneCharacters ? (
            <div style={{ marginBottom: 8 }}>
              <Text as="p" variant="bodySm" tone="subdued">
                <span style={{ fontWeight: 600 }}>*Suggested Scene Characters:</span> {sceneCharacters}
              </Text>
            </div>
          ) : null}
          <TextField
            label="Image URL"
            value={imageUrl}
            onChange={(value) => {
              if (value && uploadedFiles.length > 0) {
                setUploadedFiles([]);
              }
              setImageUrl(value);
            }}
            placeholder="https://example.com/image.png"
            autoComplete="off"
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 8px' }}>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
            <Text as="span" tone="subdued">-OR-</Text>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
          </div>

          <DropZone accept="image/*" allowMultiple={false} onDrop={handleDrop} variableHeight>
            <div style={{ padding: 16, textAlign: 'center', width: '100%' }}>
              {firstPreviewUrl ? (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <button
                    onClick={() => setUploadedFiles([])}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: 18,
                      fontWeight: 'bold',
                      zIndex: 2,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                    }}
                    aria-label="Remove image"
                    title="Remove image"
                  >
                    ×
                  </button>
                  <img
                    src={firstPreviewUrl}
                    alt="Uploaded preview"
                    style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8 }}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <Icon source={UploadMajor} tone="base" />
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Drag and drop an image here, or click to upload
                  </Text>
                </div>
              )}
            </div>
          </DropZone>



          <TextField
            label="Prompt"
            value={multiText}
            onChange={setMultiText}
            multiline={4}
            autoComplete="off"
          />

          {/* Enhance Prompt removed for Edit page */}

          {typeof onSceneTextChange === 'function' ? (
            <TextField
              label="Scene Text"
              value={sceneText || ''}
              onChange={onSceneTextChange}
              multiline={2}
              autoComplete="off"
            />
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button icon={MagicMinor} variant="primary" loading={isGenerating} onClick={handleGenerate} style={{ minWidth: 140 }}>
              Edit
            </Button>
          </div>

          {/* Upscale group (own line): model toggle + Upscale button */}
          {enableUpscaleControls ? (
            <div className="NoMarginButtonRow" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', marginTop: 0, marginBottom: 0 }}>
              <ButtonGroup segmented>
                <Button
                  pressed={upscaleModel === 'anime'}
                  onClick={() => setUpscaleModel('anime')}
                  style={{ margin: 0, minWidth: 120 }}
                >
                  Anime
                </Button>
                <Button
                  pressed={upscaleModel === 'reality'}
                  onClick={() => setUpscaleModel('reality')}
                  style={{ margin: 0, minWidth: 120 }}
                >
                  Reality
                </Button>
              </ButtonGroup>
              <Button icon={ExploreImagesMajor} variant="primary" loading={isGenerating} onClick={handleUpscale} style={{ margin: 0, minWidth: 140 }}>
                Upscale
              </Button>
            </div>
          ) : null}

          {enableExpandControls ? (
            <div className="NoMarginButtonRow" style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', marginTop: 0, marginBottom: 0 }}>
              {/* Expand group (own line): direction toggle + Expand button */}
              <ButtonGroup segmented>
                <Button
                  pressed={expandDirection === 'left'}
                  onClick={() => setExpandDirection('left')}
                  accessibilityLabel="Expand direction left"
                  style={{ margin: 0, minWidth: 120 }}
                >
                  <Icon source={ChevronLeftMinor} tone="base" />
                  <span style={{ marginLeft: 6 }}>Left</span>
                </Button>
                <Button
                  pressed={expandDirection === 'right'}
                  onClick={() => setExpandDirection('right')}
                  accessibilityLabel="Expand direction right"
                  style={{ margin: 0, minWidth: 120 }}
                >
                  <span style={{ marginRight: 6 }}>Right</span>
                  <Icon source={ChevronRightMinor} tone="base" />
                </Button>
              </ButtonGroup>
              <Button icon={MaximizeMinor} variant="primary" loading={isGenerating} onClick={handleExpandClick} style={{ margin: 0, minWidth: 140 }}>
                Expand
              </Button>
            </div>
          ) : null}
        </Box>
        <Box
          padding="400"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="0"
          minHeight="1024px"
          style={{ width: '66.6667%', maxHeight: embedded ? '70vh' : '100vh', overflow: 'hidden' }}
          className="Polaris-ShadowBevel"
        >
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text variant="headingMd" as="h2">Preview</Text>
            <InlineStack gap="200" align="center">
              {typeof renderActions === 'function' ? renderActions() : null}
              <Button icon={StarFilledMinor} onClick={handleAddToFavourites} loading={isFavouriting}>
                Add to Favourite
              </Button>
              <Button icon={ImportMinor} onClick={handleDownloadPreview} disabled={!selectedPreviewUrl}>
                Download
              </Button>
            </InlineStack>
          </div>
          <div
            style={{
              width: '100%',
              height: 'calc(100% - 40px)',
              background: '#f5f6f7',
              borderRadius: 12,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
            ref={previewContainerRef}
            onMouseDown={(e) => { if (previewType === 'upscale') { setIsDragging(true); const rect = e.currentTarget.getBoundingClientRect(); const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)); setSliderRatio(ratio); } }}
            onMouseMove={(e) => { if (previewType === 'upscale' && isDragging) { const rect = e.currentTarget.getBoundingClientRect(); const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)); setSliderRatio(ratio); } }}
            onMouseUp={() => { if (previewType === 'upscale') setIsDragging(false); }}
            onMouseLeave={() => { if (previewType === 'upscale') setIsDragging(false); }}
            onTouchStart={(e) => { if (previewType === 'upscale') { setIsDragging(true); const t = e.touches?.[0]; if (t) { const rect = e.currentTarget.getBoundingClientRect(); const ratio = Math.max(0, Math.min(1, (t.clientX - rect.left) / rect.width)); setSliderRatio(ratio); } } }}
            onTouchMove={(e) => { if (previewType === 'upscale' && isDragging) { const t = e.touches?.[0]; if (t) { const rect = e.currentTarget.getBoundingClientRect(); const ratio = Math.max(0, Math.min(1, (t.clientX - rect.left) / rect.width)); setSliderRatio(ratio); } } }}
            onTouchEnd={() => { if (previewType === 'upscale') setIsDragging(false); }}
          >
            {selectedPreviewUrl ? (
              previewType === 'upscale' && imageUrl && selectedPreviewUrl ? (
                <div style={{ position: 'absolute', inset: 0 }}>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${selectedPreviewUrl})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                      WebkitClipPath: `polygon(0 0, ${Math.round(sliderRatio * 100)}% 0, ${Math.round(sliderRatio * 100)}% 100%, 0 100%)`,
                      clipPath: `polygon(0 0, ${Math.round(sliderRatio * 100)}% 0, ${Math.round(sliderRatio * 100)}% 100%, 0 100%)`,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${Math.round(sliderRatio * 100)}%`,
                      transform: 'translateX(-50%)',
                      width: 2,
                      background: 'rgba(0,0,0,0.35)',
                      boxShadow: '0 0 0 1px rgba(255,255,255,0.6)',
                      cursor: 'ew-resize',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'white',
                        boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
                        border: '1px solid rgba(0,0,0,0.2)'
                      }}
                    />
                  </div>
                  {/* Labels for upscale before/after */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      padding: '4px 8px',
                      background: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      zIndex: 2,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    After
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      padding: '4px 8px',
                      background: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      zIndex: 2,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    Before
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${selectedPreviewUrl})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              )
            ) : (
              <Text tone="subdued">No image selected</Text>
            )}
            {typeof renderOverlay === 'function' ? renderOverlay({ previewType, expandDirection: (previewType === 'expand' ? (previewExpandDirection || expandDirection) : ''), imageRect }) : null}
            {isGenerating && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size="large" />
              </div>
            )}
          </div>
        </Box>
      </InlineStack>
      <Box
        padding="400"
        borderWidth="0"
        borderColor="transparent"
        borderRadius="0"
        minHeight="1024px"
        className="Polaris-ShadowBevel"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Text variant="headingMd" as="h2">Recent Generation</Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              icon={ChevronLeftMinor}
              onClick={() => setRecentStartIndex((idx) => Math.max(0, idx - 10))}
              disabled={recentStartIndex === 0}
            />
            <Button
              icon={ChevronRightMinor}
              onClick={() => setRecentStartIndex((idx) => Math.min(Math.max(0, recentDesigns.length - 10), idx + 10))}
              disabled={recentDesigns.length <= 10 || recentStartIndex >= Math.max(0, recentDesigns.length - 10)}
            />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 16 }}>
          {(() => {
            const tiles = new Array(10).fill(null).map((_, i) => recentDesigns[recentStartIndex + i] || null);
            return tiles.map((item, idx) => (
              <div
                key={item?._id || `empty-${idx}`}
                className="Polaris-ShadowBevel"
                style={{
                  padding: 0,
                  width: '100%',
                  aspectRatio: '1 / 1',
                  overflow: 'hidden',
                  background: '#f5f6f7',
                  display: 'block',
                  cursor: item ? 'pointer' : 'default',
                }}
                role={item ? 'button' : undefined}
                tabIndex={item ? 0 : undefined}
                onClick={item ? () => handleRecentClick(item) : undefined}
                onKeyDown={item ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRecentClick(item); } } : undefined}
              >
                {item ? (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${item.outputImageUrl})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  />
                ) : null}
              </div>
            ));
          })()}
        </div>
      </Box>
    </div>
  );
});

export default SnapsAIEditPage2;




